import { utils } from '../../utils';

/**
 * Manages the retrieval of component tracking data from the DOM. Therefore, it iterates all components on a page.
 * For each component, calls registered callbacks, which add attributes to the component tracking object.
 */
class ComponentService {
	/**
	 * @constructor singleton
	 */
	constructor() {
		if (ComponentService._instance) {
			return ComponentService._instance;
		}

		this.registeredCallbacks = [];

		ComponentService._instance = this;
	}

	/**
	 * Adds callback_ to an internal array, that starts with index 1.
	 * If priority_ is true, callback_ is stored at index 0.
	 * So when it comes to merging the data returned by every registered callback, the priority one goes first.
	 * @param {Function} callback_ - adds data to the component tracking by returning them as Object or Array
	 * @param {boolean} priority_ - indicates the default callback
	 * @returns {void}
	 */
	register(callback_, priority_ = false) {
		if (typeof callback_ !== 'function') {
			throw new Error('bad parameter: callback_ is not of type function');
		}

		const index =
			this.registeredCallbacks.length === 0
				? 1
				: this.registeredCallbacks.length;

		if (priority_) {
			this.registeredCallbacks[0] = callback_;
		} else {
			this.registeredCallbacks[index] = callback_;
		}
	}

	/**
	 * The counterpart to register().
	 * @param {Function} callback_ - adds data to the component tracking by returning them as Object or Array
	 * @returns {void}
	 */
	unregister(callback_) {
		let index = this.registeredCallbacks.indexOf(callback_);

		if (index !== -1) {
			this.registeredCallbacks.splice(index, 1);
		}
	}

	// preserve component array for components that are already on the page
	// this is needed for the in-page navigation where no real page reloads happen
	// otherwise the feature apps would be removed, which register before the classic AEM modules
	getComponentArray() {
		let newCompentList = [];
		const featureApps = document.querySelectorAll('feature-app');
		let currentComponentList = window.digitalData.component;

		// feature-app-user-menu lives inside of one-header and
		// needs to be added (when present) additionally
		if (
			currentComponentList &&
			typeof currentComponentList !== 'undefined'
		) {
			for (
				let componentIdx = 0;
				componentIdx < currentComponentList.length;
				componentIdx++
			) {
				const componentName =
					currentComponentList[componentIdx].componentInfo
						.componentName;
				if (componentName === 'audi-user-menu') {
					newCompentList.push(currentComponentList[componentIdx]);
					break;
				}
			}
		}

		if (
			currentComponentList &&
			typeof currentComponentList !== 'undefined'
		) {
			for (let appIdx = 0; appIdx < featureApps.length; appIdx++) {
				const featureApp = featureApps[appIdx];
				// do not add feature apps that have the data-audi-core-tracking-include attribute set to false
				const shouldBeTracked =
					!featureApp.hasAttribute(
						'data-audi-core-tracking-include',
					) ||
					featureApp.getAttribute(
						'data-audi-core-tracking-include',
					) === 'true';
				// do not add feature-app tags with the data-module attribute, because they will be added afterwards
				if (
					shouldBeTracked &&
					!featureApp.hasAttribute('data-module')
				) {
					const featureAppID = featureApps[appIdx].id;
					for (
						let cmpIdx = 0;
						cmpIdx < currentComponentList.length;
						cmpIdx++
					) {
						if (
							currentComponentList[cmpIdx].componentInfo &&
							currentComponentList[cmpIdx].componentInfo
								.componentID == featureAppID
						) {
							newCompentList.push(currentComponentList[cmpIdx]);
							break;
						}
					}
				}
			}
		}
		return newCompentList;
	}

	/**
	 * Iterates all components on a page. For each component, awaits registered callbacks and adds the data of their
	 * evaluated Promises to the component tracking array.
	 * @returns {Promise<Array>} - Promise that evaluates to component tracking data
	 */
	async getData() {
		const componentElements = document.querySelectorAll('[data-module]');
		const componentElementsLength = componentElements.length;

		// KONG-242: Tracking | Feature Apps are removed from Component Array when navigating between Configurator Pages
		// Do not start with empty array in case of in-page navigation.
		// Keep those feature apps that are on the page already.
		let res = this.getComponentArray();
		for (
			let componentIndex = 0;
			componentIndex < componentElementsLength;
			componentIndex += 1
		) {
			const componentObjects = [];
			const componentCollections = [];

			for (let getComponentData of this.registeredCallbacks) {
				// note: typeof callback has been checked when registered
				const callbackResult = await getComponentData(
					componentElements[componentIndex],
					componentIndex,
				);

				if (
					callbackResult instanceof Array &&
					callbackResult.length > 0
				) {
					componentCollections.push(callbackResult);
				}
				// note: typeof Array is object as well
				else if (
					typeof callbackResult === 'object' &&
					callbackResult !== []
				) {
					componentObjects.push(callbackResult);
				}
			}

			if (componentCollections.length === 0) {
				res.push(utils.mergeObjects(...componentObjects));
			} else {
				res = res.concat(
					utils.flattenAndMergeObjects(
						componentCollections,
						componentObjects,
					),
				);
			}
		}

		return res;
	}
}

/**
 * Singleton instance
 * @type {ComponentService}
 * @private
 * @static
 */

const componentService = new ComponentService();

export { componentService, ComponentService };
